/**
 * WeekStripView — shared 7-day horizontal strip
 *
 * Single visual primitive for both:
 *   - Notas tab    → SentimentCalendar (badge = emotion emoji or "+" icon)
 *   - Missões tab  → Encontros section (badge = check mark, lit when present)
 *
 * Both surfaces render the same card visual; they only differ in what they
 * show in the bottom badge slot. Centralising the layout here means visual
 * tweaks only need to happen in one place — and the two tabs stay in sync
 * with the Figma reference (6313:24958 / sentiment calendar).
 */

import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';

export interface WeekStripDay {
  date: string;       // YYYY-MM-DD — also the key
  dayNumber: string;  // "1".."31"
  dayName: string;    // localised short weekday, e.g. "Dom"
  badge: React.ReactNode;
  isToday?: boolean;
}

interface Props {
  days: WeekStripDay[];
  onDayPress?: (date: string) => void;
}

export default function WeekStripView({ days, onDayPress }: Props) {
  return (
    <View style={styles.calendar}>
      {days.map((day) => {
        const Container: any = onDayPress ? TouchableOpacity : View;
        return (
          <Container
            key={day.date}
            style={[styles.dayCard, day.isToday && styles.dayCardToday]}
            {...(onDayPress
              ? { onPress: () => onDayPress(day.date), activeOpacity: 0.7 }
              : {})}
          >
            <View style={styles.dayInfo}>
              <Text style={styles.dayNumber}>{day.dayNumber}</Text>
              <Text style={styles.dayName}>{day.dayName}</Text>
            </View>
            <View style={styles.badgeSlot}>{day.badge}</View>
          </Container>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  calendar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  dayCard: {
    padding: 12,
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    flex: 1,
    height: 84,
  },
  dayCardToday: {
    borderColor: '#669AB7',
    borderWidth: 1.5,
  },
  dayInfo: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  dayNumber: {
    color: '#151515',
    textAlign: 'center',
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    lineHeight: 12,
  },
  dayName: {
    color: '#151515',
    textAlign: 'center',
    fontSize: 8,
    fontFamily: 'Inter_500Medium',
    lineHeight: 8,
  },
  badgeSlot: {
    justifyContent: 'center',
    alignItems: 'center',
    height: 24,
    width: '100%',
  },
});
